/* The report screen: the reviewed poster, driven by a period picker, units toggle and (for samples)
 * a runner picker. Ported from prototypes/report.html. */
import C from '../report/charts.js';
import E from '../report/engine.js';
import PO from '../report/poster.js';
import type { Dataset, Run } from '../import/types';
import { makeReportFile } from './report-file';
import { canSharePdf } from './share-support';
import { feedbackLine } from './feedback';
import { store } from './store';
import { pctBounds, unpackHist, zonesFrom } from '../import/hr';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
/* A runner is either an imported dataset or one of the synthetic sample personas (data: null). */
export interface Runner { id: string; name: string; data: Dataset | null }
export const SAMPLE_RUNNERS: Runner[] = ['maya', 'alex', 'sam', 'jo'].map((id) => ({ id, name: E.PERSONAS[id].name, data: null }));

let detach: (() => void) | null = null;

export interface ReportHooks { saved?: boolean; onDeleteSaved?: () => void }

export function showReport(runners: Runner[], tag: string, hooks: ReportHooks = {}) {
  detach?.();
  const ids = runners.map((r) => r.id);
  let who = store.get('who', ids[0]);
  if (!ids.includes(who)) who = ids[0];
  const runner = () => runners.find((r) => r.id === who)!;
  // the name shown on the poster: the viewer's own choice if they set one, else the export's first name
  const posterName = () => store.get('name.' + who, runner().data?.athlete?.first ?? '');
  let raceId: number | null = null;
  C.UNITS.mi = store.get('mi', '0') === '1';

  $('controls').hidden = false;
  $('whoWrap').hidden = runners.length < 2;
  void tag; // where the data lives is said in the footer; the header carries the report's title
  const sel = $<HTMLSelectElement>('who');
  sel.innerHTML = runners.map((r) => `<option value="${r.id}" ${r.id === who ? 'selected' : ''}>${r.name}</option>`).join('');
  sel.onchange = () => { who = sel.value; store.set('who', who); raceId = null; syncSend(); buildPeriods(); render(); };
  document.querySelectorAll<HTMLButtonElement>('[data-u]').forEach((b) => {
    b.setAttribute('aria-pressed', String((b.dataset.u === 'mi') === C.UNITS.mi));
    b.onclick = () => { C.UNITS.mi = b.dataset.u === 'mi'; store.set('mi', C.UNITS.mi ? '1' : '0'); document.querySelectorAll('[data-u]').forEach((x) => x.setAttribute('aria-pressed', String(x === b))); render(); };
  });

  // runs the runner said aren't races (kept in this browser only), by start time
  const notRaceKey = () => 'notRace.' + who;
  const notRace = () => { try { return new Set<number>(JSON.parse(store.get(notRaceKey(), '[]'))); } catch { return new Set<number>(); } };
  const setNotRace = (s: Set<number>) => store.set(notRaceKey(), JSON.stringify([...s]));
  const unmark = (r: Run): Run => ({ ...r, isRace: false, raceName: null, kind: r.distM >= 16000 ? 'long' : 'easy' });
  // a max heart rate the runner entered redoes the zones of runs whose watch wrote none
  const enteredMax = () => { const v = Number(store.get('maxhr.' + who, '')); return v >= 120 && v <= 230 ? v : null; };
  const withMax = (d: Dataset): Dataset => {
    const mx = enteredMax();
    if (!mx) return d;
    const b = pctBounds(mx);
    return { ...d, maxHr: mx, maxHrSource: 'entered', zoneBounds: d.maxHrSource === 'watch' ? d.zoneBounds : b, runs: d.runs.map((r) => (r.hrHist ? { ...r, zones: zonesFrom(unpackHist(r.hrHist), b) } : r)) };
  };
  const dataset = () => { const raw = runner().data, d = raw ? withMax(raw) : null, nr = notRace(); return d
    ? { gen: { persona: { id: who, name: 'You', blurb: '', maxHr: d.maxHr, shoes: [], routes: [] }, runs: nr.size ? d.runs.map((r) => (r.isRace && nr.has(r.utcMs) ? unmark(r) : r)) : d.runs, end: d.end }, isReal: true, real: d }
    : { gen: E.generate(who), isReal: false, real: null }; };

  // ---------- period picker ----------
  const per = $<HTMLSelectElement>('per'), custom = $('custom'), from = $<HTMLInputElement>('from'), to = $<HTMLInputElement>('to');
  type Period = { id: string; label: string; start?: string; end?: string; raceId?: number };
  let periods: Period[] = [];
  function buildPeriods() {
    const { gen } = dataset();
    const end: string = gen.end, first: string = gen.runs[0] ? gen.runs[0].date : end;
    const back = (days: number) => E.dayKey(E.addDays(E.fromKey(end), -days));
    periods = [
      { id: 'ytd', label: `${end.slice(0, 4)} so far`, start: `${end.slice(0, 4)}-01-01`, end },
      { id: '12m', label: 'Last 12 months', start: back(364), end },
      { id: '90d', label: 'Last 90 days', start: back(89), end },
      { id: 'all', label: 'All time', start: first, end },
    ];
    // the 6 most recent half-marathon-or-longer builds; anything older is a custom date range away
    E.raceHistory(gen).filter((x: any) => x.nominal >= 21097.5).slice().reverse().slice(0, 6).forEach((x: any) => periods.push({ id: 'race-' + x.run.id, label: `Build: ${x.run.raceName.split(/,| - | – /)[0].replace(/[!.]+$/, '').replace(/\s+20\d\d$/, '')} ${x.run.date.slice(0, 4)}`, start: E.dayKey(E.addDays(E.fromKey(x.run.date), -112)), end: x.run.date, raceId: x.run.id }));
    periods.push({ id: 'custom', label: 'Custom dates…' });
    const saved = store.get('per.' + who, 'ytd');
    per.innerHTML = periods.map((p) => `<option value="${p.id}" ${p.id === saved ? 'selected' : ''}>${p.label}</option>`).join('');
    if (!periods.some((p) => p.id === saved)) per.value = 'ytd';
    from.min = to.min = first; from.max = to.max = end;
    from.value = store.get('from.' + who, periods[0].start!); to.value = store.get('to.' + who, end);
    custom.hidden = per.value !== 'custom';
  }
  per.onchange = () => { store.set('per.' + who, per.value); custom.hidden = per.value !== 'custom'; const p = periods.find((x) => x.id === per.value); raceId = p && p.raceId ? p.raceId : null; render(); };
  [from, to].forEach((inp) => (inp.onchange = () => { if (from.value && to.value && from.value <= to.value) { store.set('from.' + who, from.value); store.set('to.' + who, to.value); render(); } }));
  function currentPeriod(): Period {
    if (per.value === 'custom') return { id: 'custom', start: from.value, end: to.value, label: `${from.value} to ${to.value}` };
    return periods.find((x) => x.id === per.value) || periods[0];
  }

  const app = $('app');
  const fail = (h: string, lede: string) => { app.innerHTML = `<section class="panel night"><div class="in"><h2 class="headline">${h}</h2><p class="lede">${lede}</p></div></section>`; };
  function render() {
    const { gen, isReal, real } = dataset();
    const p = currentPeriod();
    const rpt = E.report(gen, { start: p.start, end: p.end === gen.end ? undefined : p.end });
    if (!rpt.totals.runs) return fail('No runs <em>in this period</em>', 'Pick a different period in the menu above.');
    const fmt = (k: string) => { const d = E.fromKey(k); return `${d.getUTCDate()} ${E.MONTHS[d.getUTCMonth()].slice(0, 3)} ${d.getUTCFullYear()}`; };
    let ps: any[];
    const notRaceFn = isReal ? (utcMs: number) => { const s = notRace(); s.add(utcMs); setNotRace(s); raceId = null; buildPeriods(); render(); } : null;
    try {
      ps = PO.panels(gen, rpt, {
        real: isReal, maxHr: isReal ? real!.maxHr : null, maxHrSource: isReal ? real!.maxHrSource : null,
        zoneBounds: isReal ? real!.zoneBounds : (gen.persona.maxHr ? [0.5, 0.6, 0.7, 0.8, 0.9, 1].map((f) => Math.round(f * gen.persona.maxHr)) : null),
        periodLabel: `${fmt(rpt.start)} – ${fmt(rpt.end)}`, raceId: raceId || p.raceId, runnerName: isReal ? posterName() : null,
        periodKind: p.id.startsWith('race-') ? 'build' : p.id,
        onRace: (id: number) => { raceId = id; render(); document.getElementById('raceday')?.scrollIntoView({ block: 'start' }); },
        onNotRace: notRaceFn,
      });
    } catch (err) { console.error(err); return fail('Something went wrong <em>with this period</em>', 'The report couldn’t be built for these dates. Try another period.'); }
    app.innerHTML = '';
    const mounts: (() => void)[] = [];
    for (const pnl of ps) {
      const s = document.createElement('section'); s.className = `panel ${pnl.theme}`; s.id = pnl.id;
      s.innerHTML = `<div class="in">${pnl.html}</div>`;
      app.appendChild(s);
      if (pnl.mount) mounts.push(() => pnl.mount(s));
    }
    mounts.forEach((m) => m());
    app.querySelectorAll<HTMLButtonElement>('[data-not-race]').forEach((b) => (b.onclick = () => notRaceFn?.(Number(b.dataset.notRace))));
    PO.highlight(app);
    PO.fit(app);
    // the report's title in the header (and the browser tab): 'Running report for Akshay'
    const r = runner(), nm = r.data ? posterName() : `${r.name.replace(/ (sample)$/, '')} (sample)`;
    $('srcTag').textContent = nm ? `Running report for ${nm}` : 'Running report';
    if (!printing) document.title = `Mileposts · ${$('srcTag').textContent}`;
    // the header isn't printed, so the PDF's first page carries the title itself
    app.querySelector('#hero .in')?.insertAdjacentHTML('afterbegin', `<div class="print-title"><span class="tape" aria-hidden="true"></span>Mileposts<span>${$('srcTag').textContent.replace(/[<>&]/g, '')}</span></div>`);
    $('foot').innerHTML = isReal ? footer(real!) : '<span>Every number here comes from a synthetic runner, not a real person. Not medical or coaching advice.</span>' + feedbackLine();
    if (isReal) {
      // the name on the poster: first name by default; change it, or clear it to show 'Your running'
      $('foot').insertAdjacentHTML('afterbegin', `<label class="name-line">Name on the poster <input id="nameIn" maxlength="40" autocomplete="off" placeholder="${runner().data?.athlete ? 'none (shows “Your running”)' : 'Add your name'}" value="${posterName().replace(/["<>&]/g, '')}"></label>`);
      $<HTMLInputElement>('nameIn').onchange = (e) => { store.set('name.' + who, (e.target as HTMLInputElement).value.trim()); render(); };
      // max heart rate: only offered when some runs have heart rate the watch didn't sort into zones
      const raw = runner().data!;
      if (raw.runs.some((r) => r.hrHist)) {
        const hint = raw.maxHrSource === 'estimated' ? `estimated ${raw.maxHr} from your runs` : raw.maxHrSource === 'watch' ? `your watch: ${raw.maxHr}` : 'enter it to see your zones';
        $('nameIn').parentElement!.insertAdjacentHTML('afterend', `<label class="name-line">Max heart rate, for zones <input id="maxIn" inputmode="numeric" maxlength="3" autocomplete="off" placeholder="${hint}" value="${enteredMax() ?? ''}"></label>`);
        $<HTMLInputElement>('maxIn').onchange = (e) => { const v = (e.target as HTMLInputElement).value.trim(); store.set('maxhr.' + who, /^\d{3}$/.test(v) ? v : ''); render(); };
      }
    }
    if (isReal && hooks.saved && hooks.onDeleteSaved) {
      $('foot').insertAdjacentHTML('afterbegin', '<span class="undo-line">Saved on this device. <button type="button" id="delSavedFoot">Delete saved data</button></span>');
      $('delSavedFoot').onclick = hooks.onDeleteSaved;
    }
    const nr = isReal ? notRace().size : 0;
    if (nr) {
      $('foot').insertAdjacentHTML('afterbegin', `<span class="undo-line">You marked ${plural(nr, 'run')} as not ${nr === 1 ? 'a race' : 'races'}. <button type="button" id="undoNotRace">Undo</button></span>`);
      $('undoNotRace').onclick = () => { setNotRace(new Set()); buildPeriods(); render(); };
    }
  }

  let rt: number | undefined, lastW = window.innerWidth, printing = false;
  const onResize = () => { if (printing || Math.abs(window.innerWidth - lastW) < 40) return; lastW = window.innerWidth; clearTimeout(rt); rt = window.setTimeout(render, 200); };
  window.addEventListener('resize', onResize);

  // ---------- PDF: the browser's own "Save as PDF", with the poster redrawn at paper width ----------
  // (vector text and charts with the real fonts; nothing leaves the browser)
  const docTitle = document.title;
  const beforePrint = () => {
    if (printing) return;
    printing = true;
    document.documentElement.classList.add('printing');
    const p = currentPeriod(), r = runner();
    const nm = r.data ? posterName() : r.name;
    document.title = `Mileposts · ${nm ? nm + ' · ' : ''}${p.label}`; // the browser's suggested file name
    render();
  };
  const afterPrint = () => {
    if (!printing) return;
    printing = false;
    document.documentElement.classList.remove('printing');
    document.title = docTitle;
    render();
  };
  window.addEventListener('beforeprint', beforePrint);
  window.addEventListener('afterprint', afterPrint);
  // phones: the whole report as a PDF file for the share sheet; computers: the print route
  const phoneShare = canSharePdf();
  $('pdf').textContent = phoneShare ? 'Share' : 'Save PDF';
  $('pdf').title = phoneShare ? 'Share the whole report as a PDF' : 'Opens your browser’s print window: choose “Save as PDF”';
  $('pdf').onclick = phoneShare ? () => sharePdf() : () => { beforePrint(); window.print(); };

  // computers: carry the finished report to a phone as a small file (no server involved)
  const sendBtn = $<HTMLButtonElement>('send');
  const touch = (() => { try { return matchMedia('(pointer: coarse)').matches; } catch { return false; } })();
  const syncSend = () => { sendBtn.hidden = phoneShare || touch || !runner().data; }; // phones receive the file, they don't send it
  sendBtn.onclick = () => {
    const d = runner().data; if (!d) return;
    const file = makeReportFile(d, [...notRace()], posterName()), url = URL.createObjectURL(file);
    // the share sheet gets the same report as plain text: browsers refuse to share .json files
    const shareFile = makeReportFile(d, [...notRace()], posterName(), 'txt');
    const canShare = (() => { try { return !!navigator.canShare && navigator.canShare({ files: [shareFile] }); } catch { return false; } })();
    const sheet = document.createElement('div');
    sheet.className = 'share-sheet'; sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-modal', 'true'); sheet.setAttribute('aria-label', 'Send to phone');
    sheet.innerHTML = `<div class="share-card">
      <p class="share-t">Your report file is ready · ${(file.size / 1048576).toFixed(1)} MB</p>
      <ol class="send-steps"><li>Send it to your phone: AirDrop, email it to yourself, or put it in iCloud or Google Drive.</li><li>On your phone, open <b>${location.host || 'Mileposts'}</b> and choose the file.</li></ol>
      <p class="send-note">It holds your report’s numbers (no GPS routes, not your exports). Only send it to yourself or people you trust.</p>
      <div class="share-btns">${canShare ? '<button type="button" class="go small" id="sendShare">Share…</button>' : ''}<a class="${canShare ? 'ghost' : 'go small'}" href="${url}" download="${file.name}">Download</a></div>
      <button type="button" class="share-x" aria-label="Close">×</button></div>`;
    document.body.appendChild(sheet);
    const close = dismissible(sheet, () => URL.revokeObjectURL(url));
    const sh = sheet.querySelector<HTMLButtonElement>('#sendShare');
    if (sh) sh.onclick = async () => {
      try { await navigator.share({ files: [shareFile], title: 'Mileposts report' }); close(); }
      catch (e) {
        if ((e as Error).name === 'AbortError') return; // the runner closed the share window
        // sharing isn't available here after all: say so, and make Download the main button
        sheet.querySelector('.share-t')!.textContent = 'Sharing isn’t available in this browser. Download the file instead, then send it to your phone.';
        sh.remove();
        sheet.querySelector('a[download]')!.className = 'go small';
      }
    };
  };

  /* Pop-up sheets close with ×, Esc, or a click outside the card. Returns the close function. */
  function dismissible(sheet: HTMLElement, onClose?: () => void) {
    const close = () => { sheet.remove(); document.removeEventListener('keydown', onKey); onClose?.(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    sheet.addEventListener('click', (e) => { if (e.target === sheet) close(); });
    (sheet.querySelector('.share-x') as HTMLButtonElement).onclick = close;
    return close;
  }

  async function sharePdf() {
    const p = currentPeriod(), r = runner();
    const nm = r.data ? posterName() : r.name;
    const name = `Mileposts - ${nm ? nm + ' - ' : ''}${p.label}`.replace(/[\\/:*?"<>|]+/g, '-');
    const sheet = document.createElement('div');
    sheet.className = 'share-sheet'; sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-modal', 'true'); sheet.setAttribute('aria-label', 'Share the report');
    sheet.innerHTML = '<div class="share-card"><p class="share-t" role="status">Preparing your PDF…</p><div class="share-btns"></div><button type="button" class="share-x" aria-label="Close">×</button></div>';
    document.body.appendChild(sheet);
    const t = sheet.querySelector('.share-t')!, btns = sheet.querySelector('.share-btns')!;
    let url: string | null = null;
    const close = dismissible(sheet, () => { if (url) URL.revokeObjectURL(url); });
    printing = true;
    document.documentElement.classList.add('printing');
    try {
      render(); // pages are drawn at paper width, the same as the printed PDF
      const { capturePages, jpegPdf } = await import('./share-pdf');
      const pages = await capturePages([...document.querySelectorAll<HTMLElement>('#app section.panel')], document.querySelector<HTMLElement>('.foot-p'), (d, n) => { t.textContent = `Preparing your PDF… page ${Math.min(d + 1, n)} of ${n}`; });
      const file = new File([jpegPdf(pages, name)], `${name}.pdf`, { type: 'application/pdf' });
      url = URL.createObjectURL(file);
      t.textContent = `Your PDF is ready · ${pages.length} pages · ${(file.size / 1048576).toFixed(1)} MB`;
      // the share sheet has to open straight from a tap, so it gets its own button
      btns.innerHTML = `<button type="button" class="go small" id="shareNow">Share PDF</button><a class="ghost" href="${url}" download="${file.name}">Download</a>`;
      (sheet.querySelector('#shareNow') as HTMLButtonElement).onclick = async () => {
        try { await navigator.share({ files: [file], title: name }); close(); }
        catch (e) { if ((e as Error).name !== 'AbortError') t.textContent = 'Sharing didn’t work on this device. Use Download instead.'; }
      };
    } catch (e) {
      console.error(e);
      t.textContent = 'The PDF couldn’t be made on this device.';
    } finally {
      printing = false;
      document.documentElement.classList.remove('printing');
      render();
    }
  }
  detach = () => { window.removeEventListener('resize', onResize); window.removeEventListener('beforeprint', beforePrint); window.removeEventListener('afterprint', afterPrint); clearTimeout(rt); };
  syncSend();
  buildPeriods();
  render();
  window.scrollTo(0, 0);
}

export function hideReport() { detach?.(); detach = null; $('controls').hidden = true; document.title = 'Mileposts'; }

const plural = (n: number, w: string) => `${n.toLocaleString()} ${w}${n === 1 ? '' : 's'}`;
/* Strava first; then what the watch export added on top of it. */
function footer(d: Dataset) {
  const c = d.counts, parts: string[] = [];
  const watchName = c.garmin ? 'Garmin' : 'watch';
  // Apple Health: what it added, said once
  const healthLine = () => {
    if (!c.health) return '';
    const bits = [];
    if (c.healthOnly) bits.push(`added ${plural(c.healthOnly, 'run')} ${c.strava ? 'Strava didn’t have' : ''}`.trim());
    if (c.healthMatched) bits.push(`matched ${plural(c.healthMatched, 'run')}${c.strava ? ' with Strava' : ''}, adding cadence and Apple’s VO2max estimate where your watch recorded them`);
    if (c.healthWeather) bits.push(`gave the weather your watch recorded on ${plural(c.healthWeather, 'run')}`);
    return bits.length ? `Your Apple Health export ${bits.join(', ')}.` : 'Your Apple Health export had nothing new.';
  };
  if (c.strava) {
    parts.push(`Built from your Strava export (${plural(c.strava, 'run')}).`);
    const added = c.garminOnly + (c.watchOnly ?? 0), filled = c.watchFilled ?? 0;
    if (c.garmin || c.watch) {
      const bits = [];
      if (added) bits.push(`added ${plural(added, 'run')} Strava didn’t have`);
      if (filled) bits.push(`filled in second-by-second detail for ${plural(filled, 'run')}`);
      if (c.matched) bits.push(`matched ${plural(c.matched, 'run')} with Strava, adding run/walk detection and VO2max where Garmin recorded them`);
      parts.push(bits.length ? `Your ${watchName} export ${bits.join(', ')}; runs in both are counted once.` : `Your ${watchName} export had nothing Strava didn’t.`);
    }
  } else if (c.health && !c.garmin && !c.watch) parts.push(`Built from your Apple Health export (${plural(d.runs.length, 'run')}). Add your Strava export for titles and races.`);
  else parts.push(`Built from your ${watchName} export (${plural(d.runs.length, 'run')}). Add your Strava export for titles and races.`);
  if (c.health && (c.strava || c.garmin || c.watch)) parts.push(healthLine());
  if (d.maxHrSource === 'estimated' && d.maxHr) parts.push(`Heart-rate zones use a max heart rate of ${d.maxHr} bpm, estimated from your runs.`);
  if (c.dupes) parts.push(`${plural(c.dupes, 'duplicate upload')} removed.`);
  if (c.review) parts.push(`${plural(c.review, 'near-match')} between sources counted once (the longer recording is used).`);
  if (c.watchUnreadable) parts.push(`${plural(c.watchUnreadable, 'watch file')} couldn’t be read and ${c.watchUnreadable === 1 ? 'was' : 'were'} skipped.`);
  if (c.weather) parts.push(`Weather for ${plural(c.weather, 'run')} from Open-Meteo (ERA5), looked up with start areas rounded to about 25 km.`);
  const left = leftOut(c.others);
  if (left) parts.push(`Left out: ${left} (Mileposts is about running).`);
  return `<span>${parts.join(' ')}</span><span>Read in your browser: nothing was uploaded. Not medical or coaching advice.</span>` + feedbackLine();
}

/* "20 hikes, 8 rides, 8 walks and 2 other activities": the most common types, then the rest. */
const ACTIVITY_WORDS: Record<string, [string, string]> = {
  Ride: ['ride', 'rides'], 'Virtual Ride': ['virtual ride', 'virtual rides'], 'E-Bike Ride': ['e-bike ride', 'e-bike rides'], Cycling: ['ride', 'rides'],
  Hike: ['hike', 'hikes'], Hiking: ['hike', 'hikes'], Walk: ['walk', 'walks'], Walking: ['walk', 'walks'],
  Swim: ['swim', 'swims'], 'Lap swimming': ['swim', 'swims'], 'Open water swimming': ['swim', 'swims'],
  Workout: ['workout', 'workouts'], 'Weight Training': ['strength session', 'strength sessions'], 'Strength training': ['strength session', 'strength sessions'],
  Yoga: ['yoga session', 'yoga sessions'], Rowing: ['row', 'rows'], Elliptical: ['elliptical session', 'elliptical sessions'],
};
function leftOut(others: Record<string, number> | undefined): string {
  if (!others) return '';
  const merged = new Map<string, [number, string, string]>();
  for (const [type, n] of Object.entries(others)) {
    const [one, many] = ACTIVITY_WORDS[type] || [`${type.toLowerCase()} activity`, `${type.toLowerCase()} activities`];
    const prev = merged.get(many);
    merged.set(many, [(prev ? prev[0] : 0) + n, one, many]);
  }
  const list = [...merged.values()].sort((a, b) => b[0] - a[0]);
  if (!list.length) return '';
  const shown = list.slice(0, 3).map(([n, one, many]) => `${n.toLocaleString()} ${n === 1 ? one : many}`);
  const rest = list.slice(3).reduce((a, [n]) => a + n, 0);
  if (rest) shown.push(`${rest.toLocaleString()} other ${rest === 1 ? 'activity' : 'activities'}`);
  return shown.length > 1 ? `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}` : shown[0];
}
